import { useState } from "react";
import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db6c46i2sgkaik1undjg";

const contacts = [
  {
    icon: "ri-mail-line",
    label: "电子邮箱",
    value: "hello@nvxing-ins-dapian.site",
    desc: "作品投稿与合作洽谈",
  },
  {
    icon: "ri-wechat-line",
    label: "微信公众号",
    value: "女性ins大片",
    desc: "每日精选作品推送",
  },
  {
    icon: "ri-instagram-line",
    label: "Instagram",
    value: "@nvxing.ins",
    desc: "海外作品与幕后花絮",
  },
  {
    icon: "ri-weibo-line",
    label: "微博",
    value: "@女性ins大片",
    desc: "话题互动与灵感征集",
  },
];

export default function ContactPage() {
  useSeo({
    title: "联系我们 - 女性ins大片作品投稿与反馈",
    description:
      "欢迎联系我们，投稿你的女性摄影作品或提供宝贵反馈。留下你的姓名、邮箱与留言，我们会尽快回复你。",
    keywords: "联系我们,女性ins大片,作品投稿,摄影合作,反馈建议",
    path: "/contact",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str === "") return;
      payload.append(key, str);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && typeof serverMsg === "string" && !isSpam
            ? serverMsg
            : "提交失败，请检查后重试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="bg-secondary-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">联系我们</span>
            </nav>
            <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
              Contact
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2">
              联系我们
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
              欢迎投稿作品、洽谈合作或给我们提出宝贵建议，我们会尽快回复你。
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact info */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                联系方式
              </h2>
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.label} className="flex items-start gap-4 bg-background-50 rounded-lg border border-background-200/70 p-4">
                    <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-primary-600 text-lg`}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">{c.label}</p>
                      <p className="text-sm font-medium text-foreground-800">{c.value}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                  给我们留言
                </h2>
                <p className="text-sm text-foreground-500 mb-6">填写下方表单，我们会在 1-2 个工作日内回复</p>

                {status === "success" ? (
                  <div className="bg-background-50 rounded-lg border border-background-200 p-8 text-center">
                    <span className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-3xl text-primary-600"></i>
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      感谢反馈，稍后会回复您
                    </h3>
                    <p className="text-sm text-foreground-500 mb-5">我们已经收到你的留言</p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form id="contact-feedback-form" onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          placeholder="请输入您的姓名"
                          className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          placeholder="请输入您的邮箱"
                          className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请输入您的留言内容（不超过 500 字）"
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                      />
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="form-field-website-alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {status === "error" && (
                      <p className="text-sm text-primary-700 bg-primary-50 rounded-md px-4 py-3">
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full sm:w-auto px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          提交留言
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}